
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { FeedbackRequest } from '../useFeedbackRequests';

export const useFeedbackActions = (request: FeedbackRequest) => {
  const navigate = useNavigate();
  const [feedbackId, setFeedbackId] = useState<string | null>(null);

  // Fetch the feedback ID when the component mounts
  useEffect(() => {
    if (request && request.id) {
      const fetchFeedbackId = async () => {
        try {
          // First check in feedback_requests table
          let { data, error } = await supabase
            .from('feedback_requests')
            .select('feedback_id')
            .eq('id', request.id)
            .maybeSingle();
          
          if (error) throw error;
          
          // If not found, check in candidate_to_recruiter_feedback_requests
          if (!data || !data.feedback_id) {
            const { data: candidateData, error: candidateError } = await supabase
              .from('candidate_to_recruiter_feedback_requests')
              .select('feedback_id')
              .eq('id', request.id)
              .maybeSingle();
            
            if (candidateError) throw candidateError;
            if (candidateData) data = candidateData;
          }
          
          if (data) setFeedbackId(data.feedback_id);
        } catch (error) {
          console.error('Error fetching feedback ID:', error);
        }
      };
      
      fetchFeedbackId();
    }
  }, [request]);

  // Handle responding to a feedback request
  const handleRespondToRequest = () => {
    try {
      if (!request.id) {
        toast.error("Identifiant de demande invalide");
        return;
      }
      
      // Navigate to feedback response form with the correct request ID
      navigate(`/feedback/${request.id}`);
    } catch (error) {
      console.error('Error navigating to response form:', error);
      toast.error("Une erreur est survenue lors de la redirection vers le formulaire de réponse");
    }
  };

  // Handle viewing a feedback or details
  const handleViewFeedback = async () => {
    try {
      // Si la demande est complétée et a un feedback_id, afficher le feedback
      if ((request.status === 'completed' || request.status === 'answered') && feedbackId) {
        navigate(`/view-feedback/${feedbackId}`);
        return;
      }
      
      // Si c'est une demande en attente, montrer les détails de la demande
      if (request.status === 'pending') {
        navigate(`/feedback/${request.id}`);
        return;
      }
      
      // Si on arrive ici, il n'y a pas d'action à prendre
      toast.error("Aucune information disponible pour cette demande");
      
    } catch (error) {
      console.error('Error navigating:', error);
      toast.error("Une erreur est survenue lors de la redirection");
    }
  };

  return {
    feedbackId,
    handleRespondToRequest,
    handleViewFeedback
  };
};
